// Kept apart from page.tsx: the route's validateSearch stays in the initial
// bundle, and page.tsx imports the v3 theme CSS.
import { z } from 'zod';

/** The page's URL state: the open judge run and the list filters. */
export const judgeSearchSchema = z.object({
    run: z.string().optional().catch(undefined),
    /** The open judge run's source events, as `start-end`. */
    source_events: z
        .string()
        .regex(/^\d+-\d+$/)
        .optional()
        .catch(undefined),
    verdict: z
        .enum(['ok', 'bad', 'urgent', 'unjudgeable', 'infrastructure_failure'])
        .optional()
        .catch(undefined),
    state: z
        .enum(['claimed', 'retrying', 'failed', 'completed', 'gap'])
        .optional()
        .catch(undefined),
    source_profile: z
        .enum(['customer_v3', 'client_sms'])
        .optional()
        .catch(undefined),
    judgment_kind: z
        .enum(['segment', 'interaction', 'compaction'])
        .optional()
        .catch(undefined),
    kind: z.enum(['all', 'regular', 'continual']).optional().catch(undefined),
    unmet_demand: z.boolean().optional().catch(undefined),
    org: z.array(z.string()).optional().catch(undefined),
    from: z.iso.date().optional().catch(undefined),
    to: z.iso.date().optional().catch(undefined),
});

export type JudgeSearch = z.infer<typeof judgeSearchSchema>;

/**
 * A trace's URL state: the judge list it was opened from, as that list's
 * resolved filters and the opened row's judgment. Previous/next walk that
 * list's order from the URL alone, so a reload or a shared link keeps it.
 */
export const judgeTraceSearchSchema = judgeSearchSchema
    .pick({
        verdict: true,
        state: true,
        source_profile: true,
        judgment_kind: true,
        kind: true,
        unmet_demand: true,
        org: true,
        from: true,
        to: true,
    })
    .extend({ judgment: z.guid().optional().catch(undefined) });

export type JudgeTraceSearch = z.infer<typeof judgeTraceSearchSchema>;

/**
 * Where the reader left the judgment list: its URL state, scroll, and open
 * rows. The list unmounts while a trace is open; Back or the trace page's
 * "Judge runs" link restores it from here.
 */
export const judgeListMemory: {
    /** The list's last URL state; null until the list renders in this tab. */
    search: JudgeSearch | null;
    /** The list context that list gives its trace links. */
    traceList: JudgeTraceSearch | null;
    scrollTop: number;
    openJudgmentIds: Set<string>;
} = { search: null, traceList: null, scrollTop: 0, openJudgmentIds: new Set() };

/** The trace page's hash for a finding's cited events, both inclusive. */
export function eventsHash({
    start,
    end,
}: Readonly<{ start: number; end: number }>): string {
    return start === end
        ? `source-event-offset-${start}`
        : `source-event-offset-${start}-${end}`;
}
